/**
 * Внешние ссылки — те, что уходят из CRM наружу: кандидату (видеоинтервью,
 * анкета) и заказчику (карточка кандидата с записью).
 *
 * Решение владелицы 29.09.2026: кандидаты пугаются чужого имени hrcrm.asia в
 * ссылке, поэтому внешние ссылки живут на адресе агентства
 * (interview.changehr.kz). Это тот же сервер и та же CRM, меняется только
 * табличка на двери. Команда работает на www.hrcrm.asia как раньше, старые
 * ссылки продолжают открываться: токен работает на любом из адресов.
 *
 * Здесь общее для браузера и сервера: какие страницы внешние и как собрать
 * адрес. Настройки читают вызывающие: браузер — VITE_PUBLIC_LINK_ORIGIN при
 * сборке, сервер — PUBLIC_LINK_HOST при запуске.
 */

/** Внешние страницы и начало их адреса. Токен — ровно один кусок после. */
export const PUBLIC_LINK_PAGES = {
  /** Кандидат: приглашение на видеоинтервью. */
  videoInterview: "/video-interview/",
  /** Кандидат: проверить и обновить свои данные в анкете. */
  candidateUpdate: "/candidate-update/",
  /** Заказчик: карточка кандидата и запись интервью. */
  interviewView: "/interview-view/",
} as const;

export type PublicLinkPage = keyof typeof PUBLIC_LINK_PAGES;

const PUBLIC_LINK_PAGE_ORDER = Object.keys(
  PUBLIC_LINK_PAGES
) as PublicLinkPage[];

/**
 * Какая внешняя страница открыта по этому пути, или null.
 *
 * Ровно один кусок после начала, как у маршрута `/video-interview/:token`
 * в App.tsx (косая черта в конце допускается, как и там). Поэтому комната
 * рекрутёра `/video-interview/41/room` внешней страницей не считается.
 */
export function publicLinkPageOf(pathname: string): PublicLinkPage | null {
  for (const page of PUBLIC_LINK_PAGE_ORDER) {
    const prefix = PUBLIC_LINK_PAGES[page];
    if (!pathname.startsWith(prefix)) continue;
    const rest = pathname.slice(prefix.length).replace(/\/$/, "");
    return rest.length > 0 && !rest.includes("/") ? page : null;
  }
  return null;
}

/** Открыта ли внешняя страница (кандидат или заказчик). */
export function isPublicLinkPage(pathname: string): boolean {
  return publicLinkPageOf(pathname) !== null;
}

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]"
  );
}

/**
 * Приводит настройку «адрес внешних ссылок» к виду `https://host[:port]`.
 *
 * Пусто или с ошибкой → null, и вызывающий берёт свой адрес, как было до
 * 29.09.2026. Принимается только голый адрес сайта: без пути, строки запроса,
 * логина и пароля. http — только для локальной проверки на своём компьютере.
 */
export function normalizePublicLinkOrigin(
  raw: string | null | undefined
): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const secure = url.protocol === "https:";
  const localHttp = url.protocol === "http:" && isLocalHostname(url.hostname);
  if (!secure && !localHttp) return null;
  if (url.username || url.password) return null;
  if (url.pathname !== "/" || url.search || url.hash) return null;
  return url.origin;
}

/**
 * Имя внешнего адреса для сервера (PUBLIC_LINK_HOST): `interview.changehr.kz`.
 * Пусто или с ошибкой → null (внешнего адреса нет, всё как раньше).
 */
export function normalizePublicLinkHost(
  raw: string | null | undefined
): string | null {
  const value = (raw ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (!value) return null;
  // Только имя: без схемы, пути и порта. Точка обязательна — «localhost»
  // внешним адресом быть не может.
  if (
    !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/.test(
      value
    )
  ) {
    return null;
  }
  return value;
}

/** Полная внешняя ссылка: адрес сайта + страница + токен. */
export function publicLinkUrl(
  page: PublicLinkPage,
  token: string,
  origin: string
): string {
  return `${origin}${PUBLIC_LINK_PAGES[page]}${encodeURIComponent(token)}`;
}
